import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import api from '../utils/api';

const Icon = ({ name, size = 16, color = 'currentColor' }) => {
  const icons = {
    refresh:  <><path d="M1 4s0-3 3-3c3 0 5 2 5 2"/><path d="M1 4v4h4"/><path d="M15 12s0 3-3 3c-3 0-5-2-5-2"/><path d="M15 12v-4h-4"/></>,
    monitor:  <><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></>,
    phone:    <><rect x="5" y="2" width="14" height="20" rx="2" ry="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></>,
    tablet:   <><rect x="4" y="2" width="16" height="20" rx="2" ry="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></>,
    users:    <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>,
    search:   <><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></>,
    eye:      <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>,
    shield:   <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></>,
    alert:    <><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></>,
    globe:    <><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></>,
    clock:    <><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></>,
    x:        <><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>,
    info:     <><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></>,
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      style={{ flexShrink: 0 }}>
      {icons[name]}
    </svg>
  );
};

const deviceIconName = (type) =>
  type === 'mobile' ? 'phone' : type === 'tablet' ? 'tablet' : 'monitor';

const deviceIconColor = (type) =>
  type === 'mobile' ? '#10b981' : type === 'tablet' ? '#4db8ff' : '#d4f535';

export default function DevicesPage() {
  const [data, setData]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [modal, setModal]         = useState(null); // { member, device, deviceIndex }
  const [lastUpdate, setLastUpdate] = useState(new Date());

  const fetchData = useCallback(async () => {
    try {
      const { data: events } = await api.get('/track/events?limit=500');
      const byEmail = {};
      events.filter(e => e.event_type === 'open' && e.member_email).forEach(e => {
        const key = e.member_email;
        if (!byEmail[key]) {
          byEmail[key] = { email: e.member_email, name: e.member_name, devices: {}, totalOpens: 0, lastOpen: e.created_at };
        }
        const deviceKey = `${e.os || 'Unknown'}__${e.browser || 'Unknown'}__${e.device_type || 'unknown'}`;
        if (!byEmail[key].devices[deviceKey]) {
          byEmail[key].devices[deviceKey] = { os: e.os, browser: e.browser, deviceType: e.device_type, ips: new Set(), count: 0, firstSeen: e.created_at, lastSeen: e.created_at };
        }
        byEmail[key].devices[deviceKey].ips.add(e.ip);
        byEmail[key].devices[deviceKey].count++;
        byEmail[key].totalOpens++;
        if (new Date(e.created_at) > new Date(byEmail[key].lastOpen)) byEmail[key].lastOpen = e.created_at;
      });
      const result = Object.values(byEmail).map(m => ({
        ...m,
        devices: Object.values(m.devices).map(d => ({ ...d, ips: Array.from(d.ips) })),
        deviceCount: Object.keys(m.devices).length,
      })).sort((a, b) => b.deviceCount - a.deviceCount);
      setData(result);
      setLastUpdate(new Date());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = data.filter(m =>
    !search ||
    m.name?.toLowerCase().includes(search.toLowerCase()) ||
    m.email?.toLowerCase().includes(search.toLowerCase()) ||
    m.devices.some(d => (d.os + d.browser).toLowerCase().includes(search.toLowerCase()))
  );

  const totalMembers  = data.length;
  const totalDevices  = data.reduce((a, m) => a + m.deviceCount, 0);
  const totalOpens    = data.reduce((a, m) => a + m.totalOpens, 0);
  const topBrowser    = (() => {
    const map = {};
    data.forEach(m => m.devices.forEach(d => { map[d.browser] = (map[d.browser]||0)+1; }));
    return Object.entries(map).sort((a,b)=>b[1]-a[1])[0]?.[0] || '—';
  })();

  return (
    <div style={{ padding: 16, background: '#0a0a0a', minHeight: '100vh' }}>

      {/* Breadcrumb + titre */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 10, color: '#555', fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 6 }}>
          Administration / Appareils
        </div>
        <h1 style={{ fontSize: 28, fontWeight: 900, color: '#ffffff', letterSpacing: -1, marginBottom: 4 }}>
          Appareils <span style={{ color: '#d4f535' }}></span>
        </h1>
        <p style={{ color: '#555', fontSize: 12 }}>Espace de contrôle documentaire</p>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 14, paddingTop: 14, borderTop: '1px solid #1a1a1a' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#d4f535', boxShadow: '0 0 8px #d4f53599' }} />
            <span style={{ fontSize: 11, color: '#888', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>
              Référence · Capture du {format(lastUpdate, 'dd MMM. yyyy').toUpperCase()}
            </span>
          </div>
          <button onClick={fetchData} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10, border: '1.5px solid #333', background: '#111', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
            <Icon name="refresh" size={14} color="#fff" /> Actualiser
          </button>
        </div>
      </div>

      {/* Info bloc */}
      <div style={{ background: '#0d1117', border: '1px solid #1e1e1e', borderLeft: '3px solid #d4f535', borderRadius: 14, padding: 14, marginBottom: 20, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <Icon name="monitor" size={18} color="#d4f535" />
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#ffffff', marginBottom: 2 }}>Analyse des appareils</div>
          <div style={{ fontSize: 11, color: '#555' }}>Appareils associés au membre · données de référence</div>
        </div>
      </div>

      {/* Alerte multi-appareils */}
      {data.filter(m => m.deviceCount > 1).length > 0 && (
        <div style={{ background: '#1a0500', border: '1px solid #ef444433', borderLeft: '3px solid #ef4444', borderRadius: 14, padding: 14, marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <Icon name="alert" size={14} color="#ef4444" />
            <span style={{ fontSize: 13, fontWeight: 700, color: '#ef4444' }}>
              {data.filter(m => m.deviceCount > 1).length} membre(s) avec plusieurs appareils détectés
            </span>
          </div>
          <div style={{ fontSize: 11, color: '#888' }}>
            Ces membres ont ouvert le document depuis des appareils différents — possible partage du code OTP.
          </div>
        </div>
      )}

      {/* 4 stats */}
      <div style={{ borderTop: '1px solid #1a1a1a', borderBottom: '1px solid #1a1a1a', padding: '16px 0', marginBottom: 20 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          {[
            { icon: 'users',   label: 'Membre visible',    value: totalMembers, color: '#d4f535' },
            { icon: 'phone',   label: 'Appareil recensé',  value: totalDevices, color: '#ffffff' },
            { icon: 'eye',     label: 'Ouverture totale',  value: totalOpens,   color: '#ffffff' },
            { icon: 'monitor', label: 'Navigateur',        value: topBrowser,   color: '#ffffff', big: false },
          ].map((s, i) => (
            <div key={i}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                <Icon name={s.icon} size={13} color="#555" />
                <span style={{ fontSize: 11, color: '#555' }}>{s.label}</span>
              </div>
              <div style={{ fontSize: s.big === false ? 22 : 36, fontWeight: 900, color: s.color, lineHeight: 1 }}>{s.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Titre section + compteur */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <h2 style={{ fontSize: 15, fontWeight: 800, color: '#ffffff' }}>Appareils par membre</h2>
        <span style={{ fontSize: 10, color: '#444', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>
          {String(filtered.length).padStart(2,'0')} FICHE DE RÉFÉRENCE
        </span>
      </div>

      {/* Recherche */}
      <div style={{ position: 'relative', marginBottom: 16 }}>
        <div style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }}>
          <Icon name="search" size={14} color="#555" />
        </div>
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Membre, appareil, navigateur..."
          style={{ width: '100%', boxSizing: 'border-box', padding: '11px 12px 11px 36px', borderRadius: 12, border: '1px solid #222', background: '#111', color: '#fff', fontSize: 13, outline: 'none' }}
        />
      </div>

      {/* Liste */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 48, color: '#555', fontSize: 13 }}>Chargement…</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, background: '#111', border: '1px solid #1e1e1e', borderRadius: 14 }}>
          <Icon name="phone" size={32} color="#333" />
          <div style={{ color: '#555', marginTop: 10, fontSize: 13 }}>Aucune donnée d'accès</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {filtered.map((m, i) => (
            <div key={i} style={{ background: '#111', border: `1px solid ${m.deviceCount > 1 ? '#ef444433' : '#1e1e1e'}`, borderLeft: `3px solid ${m.deviceCount > 1 ? '#ef4444' : '#d4f535'}`, borderRadius: 14, padding: 16 }}>

              {/* Header membre */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                <div style={{ width: 42, height: 42, borderRadius: 12, flexShrink: 0, background: '#1a1a00', border: '1px solid #d4f53533', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 900, color: '#d4f535' }}>
                  {(m.name || m.email || '?').charAt(0).toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 800, fontSize: 14, color: '#ffffff' }}>{m.name || 'Inconnu'}</div>
                  <div style={{ fontSize: 11, color: '#555', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {m.email ? m.email : 'Contact masqué'}
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 28, fontWeight: 900, color: m.deviceCount > 1 ? '#ef4444' : '#d4f535', lineHeight: 1 }}>{m.deviceCount}</div>
                  <div style={{ fontSize: 10, color: '#555' }}>appareil{m.deviceCount > 1 ? 's' : ''}</div>
                </div>
              </div>

              {/* Alerte multi-appareils */}
              {m.deviceCount > 1 && (
                <div style={{ background: '#1a0500', border: '1px solid #ef444422', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 11, color: '#ef4444', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Icon name="alert" size={12} color="#ef4444" />
                  Document ouvert depuis {m.deviceCount} appareils — possible partage du code OTP
                </div>
              )}

              {/* Séparateur */}
              <div style={{ borderTop: '1px solid #1a1a1a', marginBottom: 12 }} />

              {/* Appareils */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {m.devices.map((d, j) => (
                  <div key={j} style={{ background: '#0d0d0d', border: '1px solid #1a1a1a', borderRadius: 10, padding: '12px 14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                      <Icon name={deviceIconName(d.deviceType)} size={20} color={deviceIconColor(d.deviceType)} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#ffffff' }}>
                          {d.os || 'OS inconnu'} · {d.browser || 'Navigateur inconnu'}
                        </div>
                        <div style={{ fontSize: 11, color: '#555', marginTop: 2 }}>
                          {d.ips.length > 0 ? (
                            <span style={{ fontFamily: 'monospace', color: '#4db8ff' }}>{d.ips[0]}</span>
                          ) : 'IP inconnue'} · {j === 0 ? 'premier appareil dans la référence' : `appareil ${j + 1}`}
                        </div>
                      </div>
                      <span style={{ fontSize: 10, fontWeight: 800, padding: '3px 8px', borderRadius: 999, background: j === 0 ? '#0d1a00' : '#1a0500', color: j === 0 ? '#d4f535' : '#ef4444', border: `1px solid ${j === 0 ? '#d4f53533' : '#ef444433'}` }}>
                        {j === 0 ? '1er appareil' : `Appareil ${j + 1}`}
                      </span>
                    </div>

                    {/* Dates */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                      {[
                        { label: 'Premier accès', val: d.firstSeen },
                        { label: 'Dernier accès', val: d.lastSeen },
                      ].map((item, k) => (
                        <div key={k}>
                          <div style={{ fontSize: 10, color: '#555', marginBottom: 2 }}>{item.label}</div>
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#888' }}>
                            {format(new Date(item.val), 'dd/MM')} · {format(new Date(item.val), 'HH:mm')}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Bas : ouvertures + bouton détails */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 11, color: '#555' }}>{d.count} ouverture{d.count > 1 ? 's' : ''}</span>
                      <button onClick={() => setModal({ member: m, device: d, deviceIndex: j })}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, border: '1px solid #333', background: '#1a1a1a', color: '#ffffff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                        <Icon name="eye" size={13} color="#ffffff" /> Détails de l'appareil
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Stats bas membre */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 12 }}>
                <div style={{ background: '#0d0d0d', borderRadius: 8, padding: '10px 12px' }}>
                  <div style={{ fontSize: 22, fontWeight: 900, color: '#d4f535', lineHeight: 1 }}>{m.totalOpens}</div>
                  <div style={{ fontSize: 10, color: '#555', marginTop: 2 }}>Ouvertures totales</div>
                </div>
                <div style={{ background: '#0d0d0d', borderRadius: 8, padding: '10px 12px' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#888' }}>{format(new Date(m.lastOpen), 'dd/MM HH:mm')}</div>
                  <div style={{ fontSize: 10, color: '#555', marginTop: 2 }}>Dernier accès</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Note bas */}
      <div style={{ borderTop: '1px solid #1a1a1a', marginTop: 20, paddingTop: 16, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <Icon name="shield" size={14} color="#333" />
        <div style={{ fontSize: 11, color: '#444', lineHeight: 1.6 }}>
          Un seul appareil visible dans la capture ; aucune conclusion sur les appareils actuels ou le partage d'un compte.
        </div>
      </div>

      {/* ═══ MODAL DÉTAILS ═══ */}
      {modal && (
        <div onClick={() => setModal(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: '0 0 0 0' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#111', border: '1px solid #2a2a2a', borderRadius: '20px 20px 0 0', padding: 24, width: '100%', maxWidth: 480, maxHeight: '80vh', overflowY: 'auto' }}>

            {/* Header modal */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 900, color: '#ffffff', marginBottom: 4 }}>
                  Appareil de référence · {modal.member.name || 'Inconnu'}
                </div>
                <div style={{ fontSize: 12, color: '#555' }}>
                  {modal.device.os} · {modal.device.browser} · {modal.device.count} ouverture{modal.device.count > 1 ? 's' : ''}, le {format(new Date(modal.device.firstSeen), 'd MMMM yyyy', { locale: fr })}.
                </div>
              </div>
              <button onClick={() => setModal(null)}
                style={{ background: '#1e1e1e', border: 'none', borderRadius: 8, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, marginLeft: 12 }}>
                <Icon name="x" size={16} color="#888" />
              </button>
            </div>

            <div style={{ borderTop: '1px solid #1e1e1e', marginBottom: 16 }} />

            {/* Grille dates */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              {[
                { label: 'Premier accès', val: modal.device.firstSeen },
                { label: 'Dernier accès', val: modal.device.lastSeen },
              ].map((item, k) => (
                <div key={k}>
                  <div style={{ fontSize: 11, color: '#555', marginBottom: 4 }}>{item.label}</div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: '#ffffff' }}>
                    {format(new Date(item.val), 'dd/MM')} · {format(new Date(item.val), 'HH:mm')}
                  </div>
                </div>
              ))}
            </div>

            {/* Contact + IP */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 11, color: '#555', marginBottom: 4 }}>Contact</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: modal.member.email ? '#ffffff' : '#555' }}>
                  {modal.member.email || 'Masqué'}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#555', marginBottom: 4 }}>Adresse IP</div>
                <div style={{ fontSize: 14, fontWeight: 700, fontFamily: 'monospace', color: modal.device.ips.length > 0 ? '#4db8ff' : '#555' }}>
                  {modal.device.ips.length > 0 ? modal.device.ips.join(', ') : 'Masquée'}
                </div>
              </div>
            </div>

            <div style={{ borderTop: '1px solid #1e1e1e', marginBottom: 16 }} />

            {/* Note */}
            <div style={{ fontSize: 11, color: '#555', lineHeight: 1.7, marginBottom: 20 }}>
              Aucun identifiant technique d'appareil n'est fourni. Cet aperçu ne permet ni révocation ni réinitialisation d'un appareil réel.
            </div>

            {/* Bouton fermer */}
            <button onClick={() => setModal(null)}
              style={{ width: '100%', padding: '14px', borderRadius: 12, border: '1px solid #333', background: '#1a1a1a', color: '#ffffff', fontSize: 14, fontWeight: 800, cursor: 'pointer' }}>
              Fermer les détails
            </button>
          </div>
        </div>
      )}

    </div>
  );
}
